<template>
  <div
    class="matrix-row"
    :class="{
      'matrix-row--top-level': row.depth === 0,
      'matrix-row--group': isGroup,
      'matrix-row--focused': isFocusedRow,
    }"
  >
    <div class="matrix-row__label" :title="isGroup ? $t('roleMatrix_groupHint') : undefined">
      <span class="matrix-row__indent" :style="{ width: `${row.depth * indentPerDepth}px` }" />
      <button
        type="button"
        class="matrix-row__collapse"
        :class="{ 'matrix-row__collapse--hidden': !row.hasChildren }"
        @click.stop="$emit('toggle-collapse', row.node.id)"
      >
        <i :class="isCollapsed ? 'fa fa-chevron-right' : 'fa fa-chevron-down'" />
      </button>
      <span class="matrix-row__name">{{ label }}</span>
      <b-badge variant="secondary" class="matrix-row__kind">{{ kindLabel }}</b-badge>
    </div>
    <template v-for="role in roles">
      <MatrixCell
        v-for="(level, levelIndex) in levels"
        :key="`${role.number}-${level}`"
        :state="cellState(role, level)"
        :title="cellTitle(role, level)"
        :hint="cellHint(role)"
        :is-first-level="levelIndex === 0"
        :is-focused-column="focusedRoleNumber === role.number"
        :is-focused-cell="focusedRoleNumber === role.number && isFocusedRow"
        @select="$emit('select-cell', { roleNumber: role.number, nodeId: row.node.id })"
        @toggle="$emit('toggle-cell', { roleNumber: role.number, nodeId: row.node.id, level })"
      />
    </template>
  </div>
</template>

<script>
import MatrixCell from './MatrixCell';

const INDENT_PER_DEPTH_PX = 18;

export default {
  name: 'MatrixRow',
  components: { MatrixCell },
  props: {
    row: { type: Object, required: true },
    roles: { type: Array, required: true },
    levels: { type: Array, required: true },
    label: { type: String, required: true },
    kindLabel: { type: String, required: true },
    isCollapsed: { type: Boolean, default: false },
    isFocusedRow: { type: Boolean, default: false },
    focusedRoleNumber: { type: Number, default: null },
    cellState: { type: Function, required: true },
    cellTitle: { type: Function, required: true },
    /** Hint text per role ('' for none), e.g. a block whose view is not assigned. */
    cellHint: { type: Function, default: () => '' },
  },
  data() {
    return { indentPerDepth: INDENT_PER_DEPTH_PX };
  },
  computed: {
    /** Groups carry no permission; a tick on them switches everything inside (ADR 0005). */
    isGroup() {
      return this.row.node.kind === 'group';
    },
  },
};
</script>

<style lang="scss" scoped>
.matrix-row {
  display: flex;
  min-height: 38px;
  border-bottom: 1px solid $gray-200;

  &--top-level {
    background-color: rgba($gray-200, 0.4);
  }

  &--group .matrix-row__name {
    font-style: italic;
  }

  &--focused {
    background-color: rgba($gray-600, 0.06);
    box-shadow: inset 3px 0 0 0 $gray-600;
  }
}

.matrix-row__label {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 8px;
  width: 392px;
  padding: 0 12px;
}

.matrix-row__indent {
  flex-shrink: 0;
}

.matrix-row__collapse {
  flex-shrink: 0;
  width: 16px;
  padding: 0;
  border: none;
  background: none;
  color: $gray-500;
  font-size: 10px;

  &--hidden {
    visibility: hidden;
  }
}

.matrix-row__name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  font-size: 0.875rem;
  text-overflow: ellipsis;
  white-space: nowrap;

  .matrix-row--top-level & {
    font-weight: 600;
  }
}

.matrix-row__kind {
  flex-shrink: 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
</style>
